<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { HTMLAttributes } from 'vue'
import { cn } from '@/lib/utils'

interface Props {
  class?: HTMLAttributes['class']
  /** Visual variant of the card */
  variant?: 'default' | 'spotify' | 'music' | 'accent'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default'
})

// Unique ID for SVG defs
const uniqueId = computed(() => `bubble-card-${Math.random().toString(36).slice(2, 9)}`)

// Container ref for measuring
const containerRef = ref<HTMLDivElement | null>(null)
const measuredWidth = ref(200)
const measuredHeight = ref(60)

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  if (containerRef.value) {
    measuredWidth.value = containerRef.value.offsetWidth
    measuredHeight.value = containerRef.value.offsetHeight
    
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        measuredWidth.value = entry.contentRect.width
        measuredHeight.value = entry.contentRect.height
      }
    })
    resizeObserver.observe(containerRef.value)
  }
})

onUnmounted(() => {
  resizeObserver?.disconnect()
})

// Design constants
const chamferRatio = 0.06
const minChamfer = 4
const maxChamfer = 10
const strokeWidth = 1.5

const chamfer = computed(() => {
  const minDim = Math.min(measuredWidth.value, measuredHeight.value)
  return Math.max(minChamfer, Math.min(maxChamfer, minDim * chamferRatio))
})

const framePath = computed(() => {
  const w = measuredWidth.value
  const h = measuredHeight.value
  const c = chamfer.value
  
  return `
    M ${c} 0
    L ${w - c} 0
    L ${w} ${c}
    L ${w} ${h - c}
    L ${w - c} ${h}
    L ${c} ${h}
    L 0 ${h - c}
    L 0 ${c}
    Z
  `
})

// Gold gradient colors
const gradientColors = computed(() => ({
  start: 'var(--gold-start)',
  mid: 'var(--gold-mid)',
  end: 'var(--gold-end)'
}))
</script>

<template>
  <div
    ref="containerRef"
    :class="cn('bubble-card relative', props.class)"
  >
    <!-- SVG Frame -->
    <svg
      class="bubble-card-frame"
      :viewBox="`0 0 ${measuredWidth} ${measuredHeight}`"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient :id="`${uniqueId}-gold`" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" :stop-color="gradientColors.start" />
          <stop offset="50%" :stop-color="gradientColors.mid" />
          <stop offset="100%" :stop-color="gradientColors.end" />
        </linearGradient>
        <filter :id="`${uniqueId}-glow`" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      
      <!-- Background -->
      <path
        :d="framePath"
        fill="hsl(var(--primary) / 0.08)"
      />
      
      <!-- Border -->
      <path
        :d="framePath"
        fill="none"
        :stroke="`url(#${uniqueId}-gold)`"
        :stroke-width="strokeWidth"
        stroke-linecap="square"
        stroke-linejoin="miter"
        :filter="`url(#${uniqueId}-glow)`"
        class="bubble-card-border"
      />
    </svg>
    
    <!-- Corner accents -->
    <div class="bubble-corner bubble-corner-tl"></div>
    <div class="bubble-corner bubble-corner-tr"></div>
    <div class="bubble-corner bubble-corner-bl"></div>
    <div class="bubble-corner bubble-corner-br"></div>
    
    <!-- Content -->
    <div class="bubble-card-content">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.bubble-card {
  min-height: 48px;
}

.bubble-card-frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.bubble-card-border {
  vector-effect: non-scaling-stroke;
}

.bubble-card-content {
  position: relative;
  padding: 12px;
}

/* Corner accents */
.bubble-corner {
  position: absolute;
  width: 8px;
  height: 8px;
  pointer-events: none;
  border-color: var(--gold-solid);
  opacity: 0.6;
}

.bubble-corner-tl {
  top: 6px;
  left: 6px;
  border-top: 1px solid;
  border-left: 1px solid;
}

.bubble-corner-tr {
  top: 6px;
  right: 6px;
  border-top: 1px solid;
  border-right: 1px solid;
}

.bubble-corner-bl {
  bottom: 6px;
  left: 6px;
  border-bottom: 1px solid;
  border-left: 1px solid;
}

.bubble-corner-br {
  bottom: 6px;
  right: 6px;
  border-bottom: 1px solid;
  border-right: 1px solid;
}
</style>
